/* eslint-disable react/prop-types */

import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import _ from "lodash";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import SuiTypography from "components/SuiTypography";
import FormField from "components/FormField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Switch from "@mui/material/Switch";
import MediaPicker from "components/MediaPicker";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

import { Banner, Media } from "api";

const SortableItem = ({ banner, onEditBanner, onDelete, onUrlToggle }) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: banner.id,
  });

  const style = {
    borderBottom: "1px solid #eee",
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const cellStyle = {
    display: "flex",
    alignItems: "center",
    height: "100%",
  };

  const editBanner = () => onEditBanner(banner);
  const toggleUrl = (isActive) => onUrlToggle(isActive);

  return (
    <SuiBox py={0.1} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Grid container spacing={2}>
        <Grid item xs={1} onClick={editBanner}>
          <SuiBox sx={{ ...cellStyle, justifyContent: "center" }}>
            <MenuIcon />
          </SuiBox>
        </Grid>
        <Grid item xs={5} onClick={editBanner}>
          <SuiBox p={1}>
            <SuiBox>
              <img
                style={{ maxWidth: "100%", borderRadius: 8, border: "3px solid #eee" }}
                src={_.get(banner, "medium.s3ObjKey.Location", "")}
                alt=""
              />
            </SuiBox>
          </SuiBox>
        </Grid>
        <Grid item xs={3} onClick={editBanner}>
          <SuiBox sx={cellStyle}>
            <SuiTypography variant="button" style={{ lineBreak: "anywhere" }}>
              {banner.url}
            </SuiTypography>
          </SuiBox>
        </Grid>
        <Grid item xs={2}>
          <SuiBox sx={cellStyle}>
            <Switch
              checked={banner.externalOrInternal}
              onChange={(v) => {
                toggleUrl(v.currentTarget.checked);
              }}
            />
            <SuiBox ml={1}>
              <SuiTypography variant="button">
                {t(banner.externalOrInternal ? "Yes" : "No")}
              </SuiTypography>
            </SuiBox>
          </SuiBox>
        </Grid>
        <Grid item xs={1}>
          <SuiBox
            sx={cellStyle}
            display="inline"
            p={1}
            style={{ cursor: "pointer" }}
            onClick={() => {
              onDelete(banner);
            }}
          >
            <CloseIcon />
          </SuiBox>
        </Grid>
      </Grid>
    </SuiBox>
  );
};

function EditBanner() {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [, setLastUpdate] = useState(new Date());
  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState({});
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [currLang, setCurrLang] = useState("SC");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getCurrLangLabel = () => {
    if (currLang === "TC") {
      return t("Traditional Chinese");
    }
    if (currLang === "SC") {
      return t("Simplified Chinese");
    }
    return t("English");
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = { pageSize: 1000 };
      const rsp = await Banner.getAll(params);
      const { data } = rsp;
      setBanners(data.results.sort((a, b) => a.seq - b.seq));
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  const saveEditingBanner = async (data) => {
    const banner = { ...data };
    let error;
    if (!banner.mediaId) {
      error = "Missing banner";
    } else if (!banner.url) {
      error = "Missing URL";
    }
    if (error) {
      Swal.fire(t(error));
    } else {
      if (!banner.id) {
        banner.seq = banners.length;
        banner.status = "active";
        await Banner.create(banner);
        loadData();
      } else {
        await Banner.update(banner);
        banners[banners.findIndex((b) => b.id === banner.id)] = banner;
      }
      setEditModalOpened(false);
      setLastUpdate(new Date());
    }
  };

  const updateEditingBanner = async (prop, value, save = false) => {
    const n = { ...editingBanner };
    n[prop] = value;
    setEditingBanner(n);
    if (save) {
      await saveEditingBanner(n);
    }
  };

  useEffect(async () => {
    if (editingBanner.mediaId && _.get(editingBanner, "medium.id") !== editingBanner.mediaId) {
      const { data } = await Media.get(editingBanner.mediaId);
      updateEditingBanner("medium", data);
    }
  }, [editingBanner])

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setBanners((bs) => {
        const oldIndex = bs.map((b) => b.id).indexOf(active.id);
        const newIndex = bs.map((b) => b.id).indexOf(over.id);
        const results = arrayMove(bs, oldIndex, newIndex);
        results.forEach((r, idx) => {
          saveEditingBanner({ ...r, seq: idx + 1 });
        });
        return results;
      });
    }
  };

  const editBanner = (banner) => {
    setEditingBanner(banner);
    setEditModalOpened(true);
  };

  const deleteBanner = async (banner) => {
    const result = await Swal.mixin({
      customClass: {
        confirmButton: "button button-success",
        cancelButton: "button button-error",
      },
      buttonsStyling: false,
    }).fire({
      title: t("Confirm to delete?"),
      showCancelButton: true,
      confirmButtonText: t("Yes"),
      cancelButtonText: t("No"),
    });
    if (result.isConfirmed) {
      try {
        await Banner.del(banner.id);
        loadData();
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>{t("Homepage Banner")}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5} width="100%">
          <SuiBox width="100%">
            <Card>
              <SuiBox p={3} lineHeight={1} borderBottom="1px solid #eee">
                <SuiBox>
                  <SuiBox
                    py={1}
                    style={{
                      float: "right",
                      minWidth: 200,
                    }}
                  >
                    <SuiSelect
                      placeholder="Choose a type"
                      value={{
                        label: getCurrLangLabel(),
                        value: currLang,
                      }}
                      options={[
                        {
                          label: t("Traditional Chinese"),
                          value: "TC",
                        },
                        {
                          label: t("Simplified Chinese"),
                          value: "SC",
                        },
                        {
                          label: t("English"),
                          value: "",
                        },
                      ]}
                      onChange={(option) => {
                        setCurrLang(option.value);
                      }}
                    />
                  </SuiBox>
                  <SuiTypography variant="h5">{t("Homepage Banner")}</SuiTypography>
                  <SuiTypography variant="button" fontWeight="regular" color="text">
                    {t("Edit")}
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
              {isLoading ? (
                <>
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                  <br />
                </>
              ) : (
                <>
                  <SuiBox px={3}>
                    <FormField label={t("Widget")} disabled value={t("Homepage Banner")} />
                  </SuiBox>
                  <SuiBox py={1} px={3} style={{ background: "#eee" }}>
                    <SuiTypography variant="button">{t("Banners")}</SuiTypography>
                  </SuiBox>
                  <SuiBox>
                    <SuiBox
                      py={2}
                      borderBottom="2px solid #eee"
                      style={{ fontSize: 14, fontWeight: "bold" }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={1} />
                        <Grid item xs={5}>
                          <SuiTypography variant="button" style={{ color: "#aaa" }}>
                            {t("Image")}
                          </SuiTypography>
                        </Grid>
                        <Grid item xs={3}>
                          <SuiTypography variant="button" style={{ color: "#aaa" }}>
                            {t("URL")}
                          </SuiTypography>
                        </Grid>
                        <Grid item xs={2}>
                          <SuiTypography variant="button" style={{ color: "#aaa" }}>
                            {t("External Link?")}
                          </SuiTypography>
                        </Grid>
                        <Grid item xs={1} />
                      </Grid>
                    </SuiBox>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={banners} strategy={verticalListSortingStrategy}>
                        {banners.map((banner) => (
                          <SortableItem
                            key={banner.id}
                            id={banner.id}
                            banner={banner}
                            onEditBanner={editBanner}
                            onDelete={deleteBanner}
                            onUrlToggle={(isActive) => {
                              setEditingBanner(banner);
                              updateEditingBanner("externalOrInternal", isActive ? 1 : 0);
                              saveEditingBanner({
                                ...banner,
                                ...{ externalOrInternal: isActive ? 1 : 0 },
                              });
                            }}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                    <Grid container spacing={2}>
                      <Grid item xs={1} />
                      <Grid item xs={11}>
                        <SuiBox py={2}>
                          <SuiTypography
                            onClick={() => {
                              setEditingBanner({
                                externalOrInternal: 0,
                              });
                              setEditModalOpened(true);
                            }}
                            variant="button"
                            style={{
                              color: "#aaa",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {t("Add Banner")}
                          </SuiTypography>
                        </SuiBox>
                      </Grid>
                    </Grid>
                  </SuiBox>
                </>
              )}
            </Card>
          </SuiBox>
        </SuiBox>
        <Footer />
      </DashboardLayout>

      <Modal
        open={editModalOpened}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            setEditModalOpened(false);
          }
        }}
      >
        <SuiBox
          style={{
            position: "absolute",
            top: "25%",
            left: "50%",
            transform: "translate(-50%, -25%)",
            width: "70%",
            height: "70%",
          }}
        >
          <Card>
            <SuiBox p={2}>
              <MediaPicker
                onChange={(v) => {
                  updateEditingBanner("mediaId", v ? v.id : v);
                }}
                defaultSelectedMediaId={_.get(editingBanner, "medium.id", null)}
              />
              <FormField
                label={t("URL")}
                value={editingBanner.url || ""}
                onChange={(e) => {
                  updateEditingBanner("url", e.target.value);
                }}
              />
              <SuiBox mb={3} mt={4} ml={0.5}>
                <FormControl>
                  <SuiBox ml={1.5} mt={0.5}>
                    <RadioGroup name="radio-buttons-group" row>
                      <FormControlLabel
                        onChange={() => {
                          updateEditingBanner("externalOrInternal", 0);
                        }}
                        control={<Radio checked={editingBanner.externalOrInternal === 0} />}
                        label={
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            {t("Internal Link")}
                          </SuiTypography>
                        }
                      />
                      <SuiBox ml={3}>
                        <FormControlLabel
                          onChange={() => {
                            updateEditingBanner("externalOrInternal", 1);
                          }}
                          control={<Radio checked={editingBanner.externalOrInternal === 1} />}
                          label={
                            <SuiTypography component="label" variant="caption" fontWeight="bold">
                              {t("External Link")}
                            </SuiTypography>
                          }
                        />
                      </SuiBox>
                    </RadioGroup>
                  </SuiBox>
                </FormControl>
              </SuiBox>

              <SuiBox display="flex" justifyContent="flex-end">
                <SuiBox>
                  <SuiButton
                    onClick={() => {
                      setEditModalOpened(false);
                    }}
                  >
                    {t("Cancel")}
                  </SuiButton>
                </SuiBox>
                <SuiBox ml={2}>
                  <SuiButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      saveEditingBanner(editingBanner);
                    }}
                  >
                    {t(editingBanner.id ? "Confirm" : "Create")}
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </Card>
        </SuiBox>
      </Modal>
    </>
  );
}

export default EditBanner;
